/** @jsx jsx */
import {jsx} from 'theme-ui';
import {useState, useEffect, useRef} from 'react';
import * as styles from './styles';
import {getBaseUrl} from '../../utils/urlHelpers';

const getCurrentVersion = () => {
  if (typeof window === 'undefined') return 'v1.0';
  return new URLSearchParams(window.location.search).get('version') || 'v1.0';
};

const triggerDownload = (filename, content) => {
  const blob = new Blob([content], {type: 'application/json'});
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

// Strips leading HTTP verb from summaries e.g. "PUT Set salary..." -> "Set salary..."
const cleanName = (summary, method) => {
  if (!summary) return null;
  const prefix = method.toUpperCase() + ' ';
  return summary.startsWith(prefix) ? summary.slice(prefix.length) : summary;
};

const resolveBody = requestBody => {
  if (!requestBody || !requestBody.content) return undefined;
  const content = requestBody.content;

  if (content['application/json']) {
    return {
      mode: 'raw',
      raw: '{}',
      options: {raw: {language: 'json'}},
    };
  }

  if (content['application/x-www-form-urlencoded']) {
    const schema = content['application/x-www-form-urlencoded'].schema || {};
    const props = schema.properties || {};
    return {
      mode: 'urlencoded',
      urlencoded: Object.keys(props).map(key => ({
        key,
        value: '',
        description: (props[key] && props[key].description) || '',
        disabled: false,
      })),
    };
  }

  if (content['multipart/form-data']) {
    const schema = content['multipart/form-data'].schema || {};
    const props = schema.properties || {};
    return {
      mode: 'formdata',
      formdata: Object.keys(props).map(key => ({
        key,
        value: '',
        description: (props[key] && props[key].description) || '',
        type: 'text',
        disabled: false,
      })),
    };
  }

  return undefined;
};

// Follows an internal JSON Pointer $ref ("#/...") within the given document root
const resolveRef = (ref, root) => {
  if (!ref || !ref.startsWith('#/')) return null;
  return ref
    .slice(2)
    .split('/')
    .reduce((node, key) => (node != null ? node[key] : null), root);
};

const toPostmanCollection = (swagger, apiName) => {
  const baseUrl =
    (swagger.servers && swagger.servers[0] && swagger.servers[0].url) ||
    getBaseUrl() ||
    '';
  // Resolve relative server URLs (e.g. "/v1") against the page origin
  const resolvedBase = (() => {
    try {
      const origin =
        typeof window !== 'undefined'
          ? window.location.origin
          : 'https://placeholder.invalid';
      return new URL(baseUrl || '/', origin);
    } catch {
      return null;
    }
  })();
  const baseProtocol = resolvedBase
    ? resolvedBase.protocol.replace(':', '')
    : 'https';
  const baseHost = resolvedBase ? resolvedBase.hostname : '';
  const basePathname = resolvedBase
    ? resolvedBase.pathname.replace(/\/$/, '')
    : '';
  const items = [];
  const HTTP_METHODS = [
    'get',
    'post',
    'put',
    'patch',
    'delete',
    'head',
    'options',
  ];

  Object.entries(swagger.paths || {}).forEach(([path, pathItem]) => {
    HTTP_METHODS.forEach(method => {
      const operation = pathItem[method];
      if (!operation) return;

      const name =
        cleanName(operation.summary, method) ||
        operation.operationId ||
        `${method.toUpperCase()} ${path}`;

      const rawUrl = resolvedBase
        ? `${resolvedBase.protocol}//${resolvedBase.host}${basePathname}${path}`
        : `${baseUrl}${path}`;

      // Operation-level parameters override path-level ones with the same (name, in) key
      const mergedParams = new Map();
      for (const p of [
        ...(pathItem.parameters || []),
        ...(operation.parameters || []),
      ]) {
        const resolved = p.$ref ? resolveRef(p.$ref, swagger) ?? p : p;
        if (resolved && resolved.name && resolved.in) {
          mergedParams.set(`${resolved.in}:${resolved.name}`, resolved);
        }
      }
      const allParams = [...mergedParams.values()];

      const queryParams = allParams
        .filter(p => p.in === 'query')
        .map(p => ({
          key: p.name,
          value: '',
          description: p.description || '',
          disabled: false,
        }));

      const pathVariables = allParams
        .filter(p => p.in === 'path')
        .map(p => ({key: p.name, value: ''}));

      // Header params from the spec (e.g. X-ApplicationId, custom headers)
      const headerParams = allParams
        .filter(p => p.in === 'header')
        .map(p => ({
          key: p.name,
          value: '',
          description: p.description || '',
          disabled: false,
        }));

      const body = resolveBody(operation.requestBody);

      // Add Content-Type header based on body type
      if (body && body.mode === 'raw') {
        headerParams.push({key: 'Content-Type', value: 'application/json'});
      } else if (body && body.mode === 'urlencoded') {
        headerParams.push({
          key: 'Content-Type',
          value: 'application/x-www-form-urlencoded',
        });
      }

      const parsedPath = `${basePathname}${path}`
        .split('/')
        .filter(Boolean)
        .map(seg => seg.replace(/^\{(.+)\}$/, ':$1'));

      const request = {
        method: method.toUpperCase(),
        header: headerParams,
        url: {
          raw: rawUrl,
          protocol: baseProtocol,
          host: baseHost ? baseHost.split('.') : [],
          path: parsedPath,
          query: queryParams,
          variable: pathVariables,
        },
        description: operation.description || '',
      };
      if (body) request.body = body;

      items.push({name, request, response: []});
    });
  });

  return {
    info: {
      name: (swagger.info && swagger.info.title) || apiName,
      description: (swagger.info && swagger.info.description) || '',
      schema:
        'https://schema.getpostman.com/json/collection/v2.1.0/collection.json',
    },
    item: items,
    variable: [{key: 'baseUrl', value: baseUrl, type: 'string'}],
  };
};

export const DownloadButton = ({apiName, enabled}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    const handleOutside = e => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [enabled]);

  if (!enabled) return null;

  const fetchSwagger = () => {
    const version = getCurrentVersion();
    return fetch(
      `${getBaseUrl()}/${apiName}/swagger/${version}/swagger.json`
    ).then(r => {
      if (!r.ok)
        throw new Error(`Failed to fetch spec (${r.status} ${r.statusText})`);
      return r.json();
    });
  };

  const handleOpenApi = async () => {
    setOpen(false);
    setLoading(true);
    setError(null);
    try {
      const swagger = await fetchSwagger();
      triggerDownload(
        `${apiName}-openapi.json`,
        JSON.stringify(swagger, null, 2)
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePostman = async () => {
    setOpen(false);
    setLoading(true);
    setError(null);
    try {
      const swagger = await fetchSwagger();
      const collection = toPostmanCollection(swagger, apiName);
      triggerDownload(
        `${apiName}-postman-collection.json`,
        JSON.stringify(collection, null, 2)
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div sx={styles.wrapper} ref={ref}>
      <button
        sx={styles.button}
        onClick={() => setOpen(s => !s)}
        disabled={loading}
      >
        {loading ? 'Downloading…' : 'Download ▾'}
      </button>
      {error && <span sx={styles.error}>{error}</span>}
      {open && (
        <div sx={styles.dropdown}>
          <button sx={styles.option} onClick={handleOpenApi}>
            OpenAPI JSON
          </button>
          <button sx={styles.option} onClick={handlePostman}>
            Postman Collection v2.1
          </button>
        </div>
      )}
    </div>
  );
};
